import { apiGet, apiGetPaginated, apiPatch, apiPost, PaginatedResult } from '../client'

export type BackendApplicationStatus =
  | 'INTERVIEW_SCHEDULED'
  | 'OFFER_ACCEPTED'
  | 'OFFER_DECLINED'
  | 'OFFER_EXTENDED'
  | 'REJECTED'
  | 'SHORTLISTED'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'WITHDRAWN'

export interface BackendApplicationInterview {
  id: string
  scheduledAt: string
  status: string
  type: string
}

export interface BackendApplicationJob {
  employer?: { companyName?: string }
  id: string
  institution?: { logoUrl?: string | null; name: string }
  title: string
}

export interface BackendApplication {
  coverLetter?: string
  id: string
  interviews?: BackendApplicationInterview[]
  job?: BackendApplicationJob
  jobId: string
  offer: Offer | null
  resumeUrl?: string
  status: BackendApplicationStatus
  student?: { avatarUrl?: string | null; firstName: string; id: string; lastName: string }
  studentId: string
  submittedAt: string
}

export interface UpdateApplicationStatusPayload {
  notes?: string
  rejectionReason?: string
  status: Exclude<BackendApplicationStatus, 'INTERVIEW_SCHEDULED'>
}

export interface CreateOfferPayload {
  expiresAt?: string
  notes?: string
  offerLetterUrl?: string
  salary?: number
  salaryCurrency?: string
  salaryPeriod?: string
  startDate?: string
  title: string
}

// Inferred from the create-offer response example (only "PENDING" is documented) -
// mirrors the OFFER_EXTENDED/OFFER_ACCEPTED/OFFER_DECLINED application-status
// transitions since offer status isn't independently enumerated in the API docs.
export type OfferStatus = 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'PENDING'

export interface Offer {
  applicationId: string
  createdAt?: string
  expiresAt?: string
  id: string
  notes?: string
  offerLetterUrl?: string
  salary?: number
  salaryCurrency?: string
  salaryPeriod?: string
  startDate?: string
  status?: OfferStatus
  title: string
}

export const apply = (jobId: string) => apiPost<BackendApplication>('/applications', { jobId })

export const listMyApplications = (params?: { jobId?: string; limit?: number; page?: number; status?: BackendApplicationStatus }) =>
  apiGetPaginated<BackendApplication[]>('/applications/my', { params })

export const getApplication = (id: string) => apiGet<BackendApplication>(`/applications/${id}`)

export const withdrawApplication = (id: string) => apiPatch<BackendApplication>(`/applications/${id}/withdraw`)

export const acceptOffer = (offerId: string) => apiPatch<Offer>(`/applications/offers/${offerId}/accept`)

export const declineOffer = (offerId: string) => apiPatch<Offer>(`/applications/offers/${offerId}/decline`)

// Employer-only
/** Applications across every job the caller owns (employer or school admin) -
 * distinct from listMyApplications, which is the student's own history and 404s
 * with "Student profile not found" for any non-student caller. */
export const listApplicationsToMyJobs = (params?: { jobId?: string; limit?: number; page?: number; status?: BackendApplicationStatus }): Promise<PaginatedResult<BackendApplication[]>> =>
  apiGetPaginated<BackendApplication[]>('/applications/pipeline', { params })

export const listApplicationsForJob = (jobId: string, params?: { limit?: number; page?: number }): Promise<PaginatedResult<BackendApplication[]>> =>
  apiGetPaginated<BackendApplication[]>(`/applications/job/${jobId}`, { params })

export const updateApplicationStatus = (id: string, payload: UpdateApplicationStatusPayload) => apiPatch<BackendApplication>(`/applications/${id}/status`, payload)

export const createOffer = (applicationId: string, payload: CreateOfferPayload) => apiPost<Offer>(`/applications/${applicationId}/offer`, payload)
